import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { ChevronDownIcon, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";

interface AgentContextCardProps {
  title: string;
  content: string;
  tag: string;
  isOpen: boolean;
  onToggle: () => void;
  parseActionLog?: (content: string) => Array<{
    timestamp?: string;
    action: string;
    status?: string;
    details?: string;
  }>;
  parseWorkspace?: (content: string) => Array<{
    timestamp?: string;
    context: string;
  }>;
  parseState?: (content: string) => Array<{
    key: string;
    value: any;
    isNull: boolean;
  }>;
}

const AgentContextCard: React.FC<AgentContextCardProps> = ({
  title,
  content,
  tag,
  isOpen,
  onToggle,
  parseActionLog,
  parseWorkspace,
  parseState
}) => {
  const [showNullValues, setShowNullValues] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const renderState = (raw: string) => {
    type StateEntry = { key: string; value: string | null; description?: string; order: number };

    const entries: StateEntry[] = [];
    if (!raw) return entries;

    const lines = raw.split("\n");
    const fieldTagRegex = /<field\s+[^>]*>/i;
    const getAttr = (s: string, name: string): string | null => {
      const m = s.match(new RegExp(name + '="([\s\S]*?)"'));
      if (m) return m[1];
      const m2 = s.match(new RegExp(name + '=([^\s>/]+)'));
      return m2 ? m2[1] : null;
    };

    const isNullish = (v: string | null | undefined): boolean => {
      if (v === null || v === undefined) return true;
      const t = String(v).trim().toLowerCase();
      return t === "null" || t === "none" || t === "";
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      if (fieldTagRegex.test(trimmed)) {
        const key = getAttr(trimmed, "id") || "";
        let valueRaw = getAttr(trimmed, "value");
        const description = getAttr(trimmed, "description") || undefined;
        // Unquoted booleans/null may be captured without quotes
        if (valueRaw && /^(null|none)$/i.test(valueRaw)) valueRaw = null;
        entries.push({ key, value: valueRaw, description, order: idx });
        return;
      }

      // Fallback: key: value
      const kv = trimmed.split(":");
      if (kv.length >= 2) {
        const key = kv.shift()!.trim();
        const valueText = kv.join(":").trim();
        const value = isNullish(valueText) ? null : valueText;
        entries.push({ key, value, order: idx });
        return;
      }
    });

    // Sort non-null first, stable by original order
    entries.sort((a, b) => {
      const an = isNullish(a.value) ? 1 : 0;
      const bn = isNullish(b.value) ? 1 : 0;
      if (an !== bn) return an - bn;
      return a.order - b.order;
    });

    return entries;
  };

  return (
    <Card className="overflow-hidden">
      <CardContent 
        className="p-3 hover:bg-accent/50 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">
            {title}
          </h3>
          <ChevronDownIcon 
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              !isOpen && "-rotate-90"
            )} 
          />
        </div>
      </CardContent>
      {isOpen && (
        <CardContent className="px-3 pb-3 pt-0">
          <div className="border-t border-border pt-3 max-h-[400px] overflow-y-auto">
            {tag === "action_log" && parseActionLog ? (
              <div className="overflow-x-auto">
                {(() => {
                  const parsedLogs = parseActionLog(content);
                  return parsedLogs.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background z-10">
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground bg-background">Time</th>
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground bg-background">Action</th>
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground bg-background">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedLogs.map((entry, entryIdx) => (
                        <tr key={entryIdx} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-2 px-2 text-xs font-mono text-muted-foreground whitespace-nowrap">
                            {entry.timestamp || "-"}
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex flex-col">
                      <span className="text-sm font-medium text-foreground" title={entry.action}>
                        {entry.action.length > 18 ? entry.action.substring(0, 18) + "..." : entry.action}
                      </span>
                              {entry.details && (
                                <span className="text-xs text-muted-foreground mt-0.5">
                                  {entry.details}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            {entry.status && (
                              <span className={cn(
                                "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                                entry.status.toUpperCase() === "RUNNING" && "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
                                (entry.status.toUpperCase() === "SUCCESS" || entry.status.toUpperCase() === "COMPLETED") && "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
                                (entry.status.toUpperCase() === "FAILED" || entry.status.toUpperCase() === "ERROR") && "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400",
                                entry.status.toUpperCase() === "PENDING" && "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400"
                              )}>
                                {entry.status}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No action log entries found</p>
                  );
                })()}
              </div>
            ) : tag === "workspace" && parseWorkspace ? (
              <div className="overflow-x-auto">
                {(() => {
                  const parsedWorkspace = parseWorkspace(content);
                  return parsedWorkspace.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background z-10">
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground bg-background w-32">Time</th>
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground bg-background">Context</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedWorkspace.map((entry, entryIdx) => (
                        <tr key={entryIdx} className="border-b border-border/50">
                          <td className="py-3 px-2 text-xs font-mono text-muted-foreground whitespace-nowrap align-top w-32">
                            {entry.timestamp || "-"}
                          </td>
                          <td className="py-3 px-2">
                            <div className="bg-muted/50 dark:bg-muted/20 rounded-md p-3 border border-border/50">
                              <pre className="text-xs font-mono text-foreground whitespace-pre-wrap break-words">
{entry.context}
                              </pre>
                            </div>
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No workspace entries found</p>
                  );
                })()}
              </div>
            ) : tag === "state" && parseState ? (
              <div>
                {(() => {
                  const parsedState = parseState(content);
                  
                  // Apply filters: null values and search query
                  const filteredState = parsedState.filter(entry => {
                    // First check null filter
                    if (!showNullValues && entry.isNull) {
                      return false;
                    }
                    
                    // Then check search query
                    if (searchQuery) {
                      return entry.key.toLowerCase().includes(searchQuery.toLowerCase());
                    }
                    
                    return true;
                  });
                  
                  return parsedState.length > 0 ? (
                    <div className="space-y-3">
                      {/* Controls: Show null checkbox and search */}
                      <div className="flex items-center gap-4 pb-2 pr-1 border-b border-border">
                        {parsedState.some(entry => entry.isNull) && (
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="show-null"
                              checked={showNullValues}
                              onCheckedChange={(checked) => setShowNullValues(checked as boolean)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <label 
                              htmlFor="show-null" 
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Show Null
                            </label>
                          </div>
                        )}
                        
                        <div className="relative flex-1 max-w-xs">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                          <Input
                            type="text"
                            placeholder="Search keys..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="pl-8 h-8 text-sm"
                          />
                        </div>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-background z-10">
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-2 font-medium text-muted-foreground bg-background w-1/3 min-w-[120px]">Key</th>
                            <th className="text-left py-2 px-2 font-medium text-muted-foreground bg-background w-2/3">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredState.map((entry, entryIdx) => (
                            <tr key={entryIdx} className="border-b border-border/50 hover:bg-muted/30">
                              <td className="py-2 px-2 font-medium text-foreground w-1/3 min-w-[120px] break-all align-top">
                                {entry.key}
                              </td>
                              <td className="py-2 px-2 w-2/3 break-words">
                                <span className={cn(
                                  "text-sm whitespace-pre-wrap",
                                  entry.isNull && "text-muted-foreground italic"
                                )}>
                                  {entry.isNull 
                                    ? "null" 
                                    : typeof entry.value === "object" 
                                      ? JSON.stringify(entry.value, null, 2)
                                      : String(entry.value)
                                  }
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No state entries found</p>
                  );
                })()}
              </div>
            ) : (
              <p className="text-sm whitespace-pre-wrap">
                {content || "None"}
              </p>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default AgentContextCard;
